import { NavLink, useLocation } from "react-router-dom";
import { ElementType } from "react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

type Props = {
  path: string;
  section: string;
  showText: boolean;
  icon: ElementType;
  onClick?: () => void;
  extraIcon?: ElementType;
  isExternal?: boolean;
};

const NavigationSection = (props: Props): JSX.Element => {
  const location = useLocation();
  const isActive = location.pathname === props.path || location.pathname.startsWith(props.path + "/");

  const handleClick = (e: React.MouseEvent) => {
    if (props.onClick) {
      e.preventDefault();
      props.onClick();
      return;
    }
    if (props.isExternal) {
      e.preventDefault();
      window.open(props.path, "_blank", "noopener,noreferrer");
    }
  };

  const IconComponent = props.icon;
  const ExtraIconComponent = props.extraIcon;

  const linkClassName = cn(
    "flex w-full items-center gap-2 rounded-full px-3 py-1 text-sm font-medium transition-colors",
    props.showText ? "" : "justify-center",
    props.onClick ? "cursor-pointer hover:bg-gray-100" : ""
  );

  const content = (
    <>
      <div className="relative inline-block shrink-0">
        <IconComponent className="size-8 p-1" />
        {ExtraIconComponent && (
          <ExtraIconComponent
            className="absolute right-0 top-0 z-[2] size-4 text-orange-400"
            title="En construcción"
          />
        )}
      </div>
      {/* text-left because the button variant would otherwise centre the label.
          Wraps rather than truncates — the configuration sub-menu is only 200px
          wide and several of its labels need two lines. */}
      {props.showText ? (
        <span className="min-w-0 flex-1 text-left">{props.section}</span>
      ) : undefined}
    </>
  );

  const linkElement = props.onClick ? (
    <button
      className={linkClassName}
      onClick={handleClick}
    >
      {content}
    </button>
  ) : props.isExternal ? (
    <a
      href={props.path}
      className={linkClassName}
      onClick={handleClick}
    >
      {content}
    </a>
  ) : (
    <NavLink
      to={props.path}
      className={cn(
        linkClassName,
        isActive && "bg-palomma-primary text-white font-bold"
      )}
      onClick={handleClick}
    >
      {content}
    </NavLink>
  );

  // Expanded, the label is already on screen — a tooltip repeating it is noise.
  if (props.showText) {
    return <li className="list-none">{linkElement}</li>;
  }

  return (
    <li className="list-none">
      <Tooltip>
        <TooltipTrigger asChild>
          {linkElement}
        </TooltipTrigger>
        <TooltipContent side="right">
          {props.section}
        </TooltipContent>
      </Tooltip>
    </li>
  );
};

export default NavigationSection;
